import { Fragment, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, LogOut, Trash2, Ban, PlayCircle, ChevronDown, ChevronRight, Users, UserPlus, Music2, Check, Download, Eye, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api, { API, setToken, withToken, MAX_ACCOUNTS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { dateLocale } from "@/lib/i18n";

// Bascule de compte depuis la console : comptes connectés de l'appareil + ajout (même
// mécanisme multi-comptes que les espaces groupe — le retour se fait via le sélecteur
// de groupe classique, où la « Console Superadmin » est désormais mémorisée).
function AccountSwitchMenu() {
  const { user, accounts, memberships, switchAccount, switchGroup, setAuth, t } = useAuth();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addId, setAddId] = useState("");
  const [addPwd, setAddPwd] = useState("");
  const [addErr, setAddErr] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  // La console est une membership de l'identité (Phase 5/7) : les groupes de l'identité sont
  // proposés en bascule native (retour au groupe garanti) ; les comptes enregistrés sur
  // l'appareil n'apparaissent que s'ils ne doublonnent pas une membership.
  const myGroups = memberships.filter((m) => m.membership_id !== user?.id);
  const others = accounts.filter((a) => a.login_id !== user?.login_id
    && !memberships.some((m) => (m.group_id || m.teacher_space_id || null) === (a.group_id || null)));

  const addAccount = async (e) => {
    e.preventDefault();
    setAddErr("");
    setAddLoading(true);
    try {
      const { data } = await api.post("/auth/login", { login_id: addId, password: addPwd, remember: true });
      if (data.identity_token || data.requires_migration) {
        // Compte RANNEA (identité e-mail ou legacy à migrer) : passer par la page de connexion
        setAddErr(t("layout.add_group_identity"));
        setAddLoading(false);
        return;
      }
      setAuth(data);
      window.location.assign("/tableau-de-bord"); // un compte superadmin y est redirigé vers /superadmin
    } catch (err) {
      setAddErr(err.friendly);
      setAddLoading(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="h-10 w-10 rounded-xl border border-app t-secondary hover:text-white hover:bg-white/5 transition-colors inline-flex items-center justify-center" title={t("sa.my_accounts")} aria-label={t("sa.my_accounts")} data-testid="sa-accounts-trigger">
            <Users className="h-5 w-5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2 card-bg border-app" align="end" data-testid="sa-accounts-menu">
          <p className="text-[10px] uppercase tracking-wider t-secondary px-2 pt-1 pb-2">{t("sa.my_accounts")}</p>
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-white/5" data-testid="sa-accounts-current">
            <Check className="h-4 w-4 brand shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">Console Superadmin</p>
              <p className="text-[10px] t-secondary truncate">{user?.login_id}</p>
            </div>
          </div>
          {myGroups.map((m) => (
            <button key={m.membership_id} onClick={() => switchGroup(m.membership_id)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors" data-testid={`sa-switch-membership-${m.membership_id}`}>
              <Music2 className="h-4 w-4 t-secondary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.group_name}</p>
                <p className="text-[10px] t-secondary truncate">
                  {["admin", "music_leader", "musician", "member"].includes(m.role) ? t(`usr.role_${m.role}`) : (m.role || "")}
                  {m.instrument ? ` · ${m.instrument}` : ""}
                </p>
              </div>
            </button>
          ))}
          {others.length > 0 && (
            <p className="text-[10px] uppercase tracking-wider t-secondary px-2 pt-2 pb-1" data-testid="sa-other-accounts-label">{t("layout.other_accounts")}</p>
          )}
          {others.map((a) => (
            <button key={a.login_id} onClick={() => switchAccount(a)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors" data-testid={`sa-switch-${a.login_id}`}>
              <Music2 className="h-4 w-4 t-secondary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.group_name}</p>
                <p className="text-[10px] t-secondary truncate">{a.login_id}</p>
              </div>
            </button>
          ))}
          {others.length === 0 && myGroups.length === 0 && <p className="text-xs t-secondary px-2 py-2" data-testid="sa-accounts-empty">{t("sa.no_other_account")}</p>}
          {accounts.length < MAX_ACCOUNTS && (
            <button onClick={() => { setOpen(false); setAddOpen(true); setAddErr(""); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left text-sm brand transition-colors" data-testid="sa-accounts-add">
              <UserPlus className="h-4 w-4 shrink-0" /> {t("layout.add_group")}
            </button>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="card-bg border-app" data-testid="sa-add-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("layout.add_group_title")}</DialogTitle>
            <DialogDescription className="t-secondary">{t("layout.add_group_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={addAccount} className="space-y-3">
            {addErr && <p className="text-sm text-rose-400" data-testid="sa-add-account-error">{addErr}</p>}
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("id_label")}</Label>
              <Input value={addId} onChange={(e) => setAddId(e.target.value)} required placeholder={t("id_placeholder")} className="mt-1 bg-transparent border-app" data-testid="sa-add-account-id" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("password_label")}</Label>
              <Input type="password" value={addPwd} onChange={(e) => setAddPwd(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="sa-add-account-password" />
            </div>
            <Button type="submit" disabled={addLoading} className="w-full brand-bg text-white" data-testid="sa-add-account-submit">
              {addLoading ? t("connecting") : t("connect")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Kpi({ label, value, accent = "", testid }) {
  return (
    <div className="app-card p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider t-secondary">{label}</p>
      <p className={`text-xl font-bold font-heading mt-0.5 ${accent}`}>{value}</p>
    </div>
  );
}

function Section({ title, subtitle, open, onToggle, testid, children }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-3 text-left group" data-testid={`${testid}-toggle`}>
        <span className="p-1.5 rounded-lg border border-app t-secondary group-hover:bg-white/5 transition-colors">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span className="min-w-0">
          <span className="font-heading text-xl font-bold block">{title}</span>
          {subtitle && <span className="text-sm t-secondary block">{subtitle}</span>}
        </span>
      </button>
      {open && <div className="mt-4">{children}</div>}
    </section>
  );
}

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(dateLocale(lang), { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso, lang) => (iso ? new Date(iso).toLocaleString(dateLocale(lang), { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function SuperadminPage() {
  const { user, logout, t, lang } = useAuth();
  const [groups, setGroups] = useState(null);
  const [members, setMembers] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(null);
  const [spaces, setSpaces] = useState(null);
  const [tmembers, setTmembers] = useState({});
  const [texpanded, setTexpanded] = useState(null);
  const [tconfirmDelete, setTconfirmDelete] = useState(null);
  const [tconfirmImpersonate, setTconfirmImpersonate] = useState(null);
  const [mSearch, setMSearch] = useState("");
  const [tSearch, setTSearch] = useState("");
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [gSearch, setGSearch] = useState("");
  const [confirmImpersonate, setConfirmImpersonate] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [uSearch, setUSearch] = useState("");
  const [sec, setSec] = useState({ members: false, groups: true, teaching: false, activity: false, users: false });
  const toggleSec = (k) => setSec((s) => ({ ...s, [k]: !s[k] }));

  const load = () => {
    api.get("/superadmin/groups").then((r) => setGroups(r.data)).catch((e) => toast.error(e.friendly));
    api.get("/superadmin/teaching-spaces").then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
    api.get("/superadmin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/superadmin/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/superadmin/users/activity").then((r) => setUserActivity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleTMembers = async (sid) => {
    if (texpanded === sid) { setTexpanded(null); return; }
    setTexpanded(sid);
    if (!tmembers[sid]) {
      try {
        const r = await api.get(`/superadmin/teaching-spaces/${sid}/members`);
        setTmembers((m) => ({ ...m, [sid]: r.data }));
      } catch (e) { toast.error(e.friendly); }
    }
  };

  const removeSpace = async (sid) => {
    try {
      const r = await api.delete(`/superadmin/teaching-spaces/${sid}`);
      toast.success(t("sa.space_deleted", { name: r.data.deleted_space }));
      setTconfirmDelete(null);
      setTexpanded(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const action = async (gid, payload) => {
    try {
      await api.put(`/superadmin/groups/${gid}/subscription`, payload);
      toast.success(t("sa.action_done"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const toggleMembers = async (gid) => {
    if (expanded === gid) { setExpanded(null); return; }
    setExpanded(gid);
    if (!members[gid]) {
      try {
        const r = await api.get(`/superadmin/groups/${gid}/members`);
        setMembers((m) => ({ ...m, [gid]: r.data }));
      } catch (e) { toast.error(e.friendly); }
    }
  };

  const impersonate = async (gid) => {
    // Session de support : bascule vers l'admin du groupe (journalisée côté serveur).
    // Le compte impersonné n'est PAS mémorisé dans le registre multi-comptes.
    try {
      const r = await api.post("/superadmin/impersonate", { group_id: gid });
      setToken(r.data.token);
      try { await api.post("/auth/switch", { token: r.data.token }); } catch { /* cookie best-effort, Bearer en secours */ }
      window.location.assign("/tableau-de-bord");
    } catch (e) { toast.error(e.friendly); }
  };

  const removeGroup = async (gid) => {
    try {
      const r = await api.delete(`/superadmin/groups/${gid}`);
      toast.success(t("sa.group_deleted", { name: r.data.deleted_group }));
      setConfirmDelete(null);
      setExpanded(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const removeAccount = async (g) => {
    try {
      const r = await api.delete(`/superadmin/identities/${g.owner_identity_id}`);
      toast.success(t("sa.account_deleted", { name: r.data.deleted_email }));
      setConfirmDeleteAccount(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const personalSpaces = (groups || []).filter((g) => g.kind === "personal");
  const musicGroups = (groups || []).filter((g) => g.kind !== "personal");

  const impersonateSpace = async (sid) => {
    // Session de support sur un espace enseignement (journalisée côté serveur).
    try {
      const r = await api.post("/superadmin/impersonate-teaching", { space_id: sid });
      setToken(r.data.token);
      try { await api.post("/auth/switch", { token: r.data.token }); } catch { /* cookie best-effort, Bearer en secours */ }
      window.location.assign("/tableau-de-bord");
    } catch (e) { toast.error(e.friendly); }
  };

  const ensurePersonalSpaces = async () => {
    try {
      const r = await api.post("/superadmin/ensure-personal-spaces");
      toast.success(t("sa.ensure_personal_done", { n: r.data.total, r: r.data.removed }));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const taction = async (sid, act) => {
    try {
      await api.put(`/superadmin/teaching-spaces/${sid}/status`, { action: act });
      toast.success(t("sa.action_done"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const GroupActions = ({ g, showMembers, personal }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1.5 rounded-lg border border-app t-secondary hover:bg-white/5" data-testid={`sa-actions-${g.id}`}><MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="card-bg border-app" align="end" data-testid={`sa-actions-menu-${g.id}`}>
        {showMembers && (
          <DropdownMenuItem onSelect={() => toggleMembers(g.id)} data-testid={`sa-members-toggle-${g.id}`}>
            <Users className="h-4 w-4 mr-2" /> {t("sa.view_members")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => setConfirmImpersonate(g.id)} data-testid={`sa-impersonate-${g.id}`}>
          <Eye className="h-4 w-4 mr-2" /> {t("sa.impersonate")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => action(g.id, { action: g.suspended ? "unsuspend" : "suspend" })} data-testid={g.suspended ? `sa-unsuspend-${g.id}` : `sa-suspend-${g.id}`}>
          {g.suspended ? <PlayCircle className="h-4 w-4 mr-2" /> : <Ban className="h-4 w-4 mr-2" />} {g.suspended ? (showMembers ? t("sa.unsuspend") : t("sa.unsuspend_space")) : (showMembers ? t("sa.suspend") : t("sa.suspend_space"))}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        {personal ? (
          <DropdownMenuItem onSelect={() => setConfirmDeleteAccount(g)} disabled={!g.owner_identity_id} className="text-rose-400" data-testid={`sa-delete-account-${g.id}`}>
            <Trash2 className="h-4 w-4 mr-2" /> {t("sa.delete_account")}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => setConfirmDelete(g.id)} className="text-rose-400" data-testid={`sa-delete-${g.id}`}>
            <Trash2 className="h-4 w-4 mr-2" /> {t("usr.delete_title_attr")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Tableau des comptes individuels : nom / prénom / e-mail / création + actions admin
  const renderMembersTable = (rows, search) => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((g) => !q
      || `${g.owner_prenom || ""} ${g.owner_nom || ""}`.toLowerCase().includes(q)
      || (g.owner_email || "").toLowerCase().includes(q));
    return (
      <div className="app-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="sa-members-table">
          <thead>
            <tr className="border-b border-app text-xs uppercase tracking-wider t-secondary">
              <th className="text-left px-4 py-3">{t("sa.col_lastname")}</th>
              <th className="text-left px-3 py-3">{t("sa.col_firstname")}</th>
              <th className="text-left px-3 py-3">{t("sa.col_email")}</th>
              <th className="text-left px-3 py-3">{t("sa.col_created")}</th>
              <th className="text-right px-4 py-3">{t("usr.col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <tr key={g.id} className="border-b border-app/50" data-testid={`sa-group-row-${g.id}`}>
                <td className="px-4 py-3 font-semibold">
                  <span className="flex items-center gap-2 flex-wrap">{g.owner_nom || "—"}
                    {g.suspended && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-500/15 text-slate-300 border-slate-500/40" data-testid={`sa-status-${g.id}`}>{t("sa.st_suspended")}</span>}
                  </span>
                </td>
                <td className="px-3 py-3">{g.owner_prenom || "—"}</td>
                <td className="px-3 py-3 text-xs t-secondary" data-testid={`sa-member-email-${g.id}`}>{g.owner_email || "—"}</td>
                <td className="px-3 py-3 text-xs t-secondary">{fmtDate(g.owner_created_at || g.created_at, lang)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end"><GroupActions g={g} showMembers={false} personal /></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm t-secondary p-6 text-center" data-testid="sa-members-empty">{t("sa.empty")}</p>}
      </div>
    );
  };

  const renderGroupTable = (rows, search, showMembers, keyPrefix) => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((g) => !q || g.name.toLowerCase().includes(q) || (g.diminutif || "").toLowerCase().includes(q));
    return (
      <div className="app-card overflow-x-auto">
        <table className="w-full text-sm" data-testid={`sa-${keyPrefix}-table`}>
          <thead>
            <tr className="border-b border-app text-xs uppercase tracking-wider t-secondary">
              <th className="text-left px-4 py-3">{keyPrefix === "members" ? t("sa.col_space") : t("param.tab_group")}</th>
              <th className="text-left px-3 py-3">{t("sa.col_members")}</th>
              <th className="text-left px-3 py-3">{t("sa.col_created")}</th>
              <th className="text-right px-4 py-3">{t("usr.col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <Fragment key={g.id}>
                <tr className="border-b border-app/50" data-testid={`sa-group-row-${g.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold flex items-center gap-2 flex-wrap">{g.name}
                      {g.suspended && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-500/15 text-slate-300 border-slate-500/40" data-testid={`sa-status-${g.id}`}>{t("sa.st_suspended")}</span>}
                    </p>
                    <p className="text-xs t-secondary">@{g.diminutif} · {g.custom_style || g.style}</p>
                  </td>
                  <td className="px-3 py-3" data-testid={`sa-member-count-${g.id}`}>{g.member_count}</td>
                  <td className="px-3 py-3 text-xs t-secondary">{fmtDate(g.created_at, lang)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end"><GroupActions g={g} showMembers={showMembers} /></div>
                  </td>
                </tr>
                {showMembers && expanded === g.id && (
                  <tr className="border-b border-app/50">
                    <td colSpan={4} className="px-4 py-3 card-bg" data-testid={`sa-members-${g.id}`}>
                      {(members[g.id] || []).length === 0 ? (
                        <p className="text-xs t-secondary">{t("common.loading")}</p>
                      ) : (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                          {members[g.id].map((m) => (
                            <li key={m.id} className="text-xs flex items-center gap-2" data-testid={`sa-member-${m.id}`}>
                              <span className="font-medium">{m.prenom} {m.nom}</span>
                              <span className="t-secondary">{m.login_id}</span>
                              <span className="t-secondary">· {m.role}{m.instrument ? ` · ${m.instrument}` : ""} · {m.status}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm t-secondary p-6 text-center" data-testid={`sa-${keyPrefix}-empty`}>{t("sa.empty")}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen app-bg" data-testid="superadmin-page">
      <header className="border-b border-app px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <img src="/rannea-logo-white.png" alt="RANNEA" className="h-8 shrink-0" />
          <div className="min-w-0">
            <h1 className="font-heading text-lg font-bold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 accent shrink-0" /> <span className="truncate">{t("sa.title")}</span>
            </h1>
            <p className="text-xs t-secondary truncate">{user?.login_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <AccountSwitchMenu />
          <Button variant="outline" className="border-app" onClick={logout} data-testid="sa-logout-button">
            <LogOut className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">{t("common.logout")}</span>
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-2" data-testid="sa-stats">
            <Kpi label={t("sa.kpi_groups")} value={stats.groups_total} testid="sa-kpi-groups" />
            <Kpi label={t("sa.kpi_members")} value={stats.members_total} testid="sa-kpi-members" />
            <Kpi label={t("sa.kpi_teaching")} value={stats.teaching_spaces ?? 0} accent="text-violet-400" testid="sa-kpi-teaching" />
            <Kpi label={t("sa.kpi_active_7d")} value={stats.active_users_7d ?? 0} accent="text-sky-400" testid="sa-kpi-active-7d" />
            <Kpi label={t("sa.kpi_active_30d")} value={stats.active_users_30d ?? 0} accent="text-sky-400" testid="sa-kpi-active-30d" />
            <Kpi label={t("sa.kpi_expired")} value={(stats.by_status?.expired ?? 0) + (stats.suspended ?? 0)} accent="text-rose-400" testid="sa-kpi-expired" />
          </div>
        )}
        <Section title={t("sa.members_title", { n: personalSpaces.length })} subtitle={t("sa.members_subtitle")} open={sec.members} onToggle={() => toggleSec("members")} testid="sa-section-members">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Input value={mSearch} onChange={(e) => setMSearch(e.target.value)} placeholder={t("sa.search_group")} className="bg-transparent border-app max-w-sm" data-testid="sa-member-space-search" />
          <Button variant="outline" className="border-app" onClick={ensurePersonalSpaces} data-testid="sa-ensure-personal-button">
            <UserPlus className="h-4 w-4 mr-2" /> {t("sa.ensure_personal")}
          </Button>
        </div>
        {renderMembersTable(personalSpaces, mSearch)}
        </Section>

        <Section title={t("sa.groups_title", { n: musicGroups.length })} subtitle={t("sa.subtitle")} open={sec.groups} onToggle={() => toggleSec("groups")} testid="sa-section-groups">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Input value={gSearch} onChange={(e) => setGSearch(e.target.value)} placeholder={t("sa.search_group")} className="bg-transparent border-app max-w-sm" data-testid="sa-group-search" />
          <a href={withToken(`${API}/superadmin/groups/export.csv`)} download className="inline-flex" data-testid="sa-export-csv">
            <Button variant="outline" className="border-app" type="button"><Download className="h-4 w-4 mr-1" /> {t("sa.export_csv")}</Button>
          </a>
        </div>

        {renderGroupTable(musicGroups, gSearch, true, "groups")}
        </Section>

        <Section title={t("sa.teaching_title", { n: spaces ? spaces.length : "…" })} subtitle={t("sa.teaching_subtitle")} open={sec.teaching} onToggle={() => toggleSec("teaching")} testid="sa-section-teaching">
        <Input value={tSearch} onChange={(e) => setTSearch(e.target.value)} placeholder={t("sa.search_space")} className="bg-transparent border-app max-w-sm mb-4" data-testid="sa-space-search" />

        <div className="app-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="sa-teaching-table">
            <thead>
              <tr className="border-b border-app text-xs uppercase tracking-wider t-secondary">
                <th className="text-left px-4 py-3">{t("sa.col_space")}</th>
                <th className="text-left px-3 py-3">{t("sa.col_owner")}</th>
                <th className="text-left px-3 py-3">{t("sa.col_students")}</th>
                <th className="text-left px-3 py-3">{t("sa.col_classes")}</th>
                <th className="text-left px-3 py-3">{t("sa.col_created")}</th>
                <th className="text-right px-4 py-3">{t("usr.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {(spaces || [])
                .filter((s) => {
                  const q = tSearch.trim().toLowerCase();
                  return !q || s.name.toLowerCase().includes(q) || (s.owner_login || "").toLowerCase().includes(q) || (s.diminutif || "").includes(q);
                })
                .map((s) => (
                <Fragment key={s.id}>
                  <tr className="border-b border-app/50" data-testid={`sa-space-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold flex items-center gap-2 flex-wrap">{s.name}
                        {s.suspended && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-500/15 text-slate-300 border-slate-500/40" data-testid={`sa-space-status-${s.id}`}>{t("sa.st_suspended")}</span>}
                      </p>
                      <p className="text-xs t-secondary">@{s.diminutif}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium">{s.owner_name}</p>
                      <p className="text-xs t-secondary">{s.owner_login}</p>
                    </td>
                    <td className="px-3 py-3" data-testid={`sa-space-students-${s.id}`}>{s.students_count}</td>
                    <td className="px-3 py-3" data-testid={`sa-space-classes-${s.id}`}>{s.classes_count}</td>
                    <td className="px-3 py-3 text-xs t-secondary">{fmtDate(s.created_at, lang)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-lg border border-app t-secondary hover:bg-white/5" data-testid={`sa-space-actions-${s.id}`}><MoreVertical className="h-4 w-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="card-bg border-app" align="end" data-testid={`sa-space-actions-menu-${s.id}`}>
                            <DropdownMenuItem onSelect={() => toggleTMembers(s.id)} data-testid={`sa-space-members-toggle-${s.id}`}>
                              <Users className="h-4 w-4 mr-2" /> {t("sa.view_members")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setTconfirmImpersonate(s.id)} data-testid={`sa-space-impersonate-${s.id}`}>
                              <Eye className="h-4 w-4 mr-2" /> {t("sa.impersonate")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => taction(s.id, s.suspended ? "unsuspend" : "suspend")} data-testid={s.suspended ? `sa-space-unsuspend-${s.id}` : `sa-space-suspend-${s.id}`}>
                              {s.suspended ? <PlayCircle className="h-4 w-4 mr-2" /> : <Ban className="h-4 w-4 mr-2" />} {s.suspended ? t("sa.unsuspend") : t("sa.suspend")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem onSelect={() => setTconfirmDelete(s.id)} className="text-rose-400" data-testid={`sa-space-delete-${s.id}`}>
                              <Trash2 className="h-4 w-4 mr-2" /> {t("usr.delete_title_attr")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                  {texpanded === s.id && (
                    <tr key={`${s.id}-members`} className="border-b border-app/50">
                      <td colSpan={6} className="px-4 py-3 card-bg" data-testid={`sa-space-members-${s.id}`}>
                        {(tmembers[s.id] || []).length === 0 ? (
                          <p className="text-xs t-secondary">{t("common.loading")}</p>
                        ) : (
                          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                            {tmembers[s.id].map((m) => (
                              <li key={m.id} className="text-xs flex items-center gap-2" data-testid={`sa-space-member-${m.id}`}>
                                <span className="font-medium">{m.prenom} {m.nom}</span>
                                <span className="t-secondary">{m.login_id}</span>
                                <span className="t-secondary">· {m.role}{m.instrument ? ` · ${m.instrument}` : ""} · {m.status}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {spaces && spaces.length === 0 && <p className="text-sm t-secondary p-6 text-center" data-testid="sa-teaching-empty">{t("sa.teaching_empty")}</p>}
          {spaces && spaces.length > 0 && tSearch.trim() && (spaces || []).filter((s) => {
            const q = tSearch.trim().toLowerCase();
            return s.name.toLowerCase().includes(q) || (s.owner_login || "").toLowerCase().includes(q) || (s.diminutif || "").includes(q);
          }).length === 0 && <p className="text-sm t-secondary p-6 text-center" data-testid="sa-teaching-no-result">{t("sa.teaching_empty")}</p>}
        </div>
        </Section>

        <Section title={t("sa.activity_title")} open={sec.activity} onToggle={() => toggleSec("activity")} testid="sa-section-activity">
        <div className="grid gap-4">
          <div className="app-card p-4" data-testid="sa-activity-signups">
            <p className="t-secondary text-xs uppercase tracking-wider mb-2">{t("sa.activity_signups")}</p>
            {(activity?.signups || []).length === 0 && <p className="text-xs t-secondary">{t("sa.activity_empty")}</p>}
            <ul className="space-y-1.5">
              {(activity?.signups || []).map((s) => (
                <li key={s.id} className="text-xs flex items-center justify-between gap-2" data-testid={`sa-signup-${s.id}`}>
                  <span className="min-w-0 truncate"><span className="font-medium">{s.name}</span> <span className="t-secondary">@{s.diminutif}</span></span>
                  <span className="t-secondary shrink-0">{fmtDate(s.created_at, lang)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        </Section>

        <Section title={t("sa.users_activity_title")} subtitle={t("sa.users_activity_subtitle")} open={sec.users} onToggle={() => toggleSec("users")} testid="sa-section-users">
        <Input value={uSearch} onChange={(e) => setUSearch(e.target.value)} placeholder={t("sa.search_user")} className="bg-transparent border-app max-w-sm mb-4" data-testid="sa-user-search" />
        <div className="app-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="sa-users-activity-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider t-secondary border-b border-app">
                <th className="p-3">{t("sa.col_member")}</th>
                <th className="p-3">{t("sa.col_group")}</th>
                <th className="p-3">{t("sa.col_last_seen")}</th>
              </tr>
            </thead>
            <tbody>
              {(userActivity || [])
                .filter((u) => {
                  const q = uSearch.trim().toLowerCase();
                  return !q || `${u.prenom || ""} ${u.nom || ""}`.toLowerCase().includes(q)
                    || (u.login_id || "").toLowerCase().includes(q) || (u.group_name || "").toLowerCase().includes(q);
                })
                .map((u) => (
                  <tr key={u.id} className="border-b border-app/30" data-testid={`sa-user-activity-${u.id}`}>
                    <td className="p-3"><span className="font-medium">{u.prenom} {u.nom}</span> <span className="t-secondary text-xs">{u.login_id}</span></td>
                    <td className="p-3 t-secondary text-xs">{u.group_name}</td>
                    <td className="p-3 text-xs" data-testid={`sa-user-last-seen-${u.id}`}>{u.last_seen_at ? fmtDateTime(u.last_seen_at, lang) : <span className="t-secondary">{t("sa.never_seen")}</span>}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {userActivity && userActivity.length === 0 && <p className="p-4 text-sm t-secondary">{t("sa.activity_empty")}</p>}
        </div>
        </Section>

        <Dialog open={!!confirmImpersonate} onOpenChange={(o) => { if (!o) setConfirmImpersonate(null); }}>
          <DialogContent className="card-bg border-app" data-testid="sa-impersonate-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("sa.impersonate")}</DialogTitle>
              <DialogDescription className="t-secondary">{t("sa.impersonate_q")}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-app" onClick={() => setConfirmImpersonate(null)} data-testid="sa-impersonate-cancel">{t("common.cancel")}</Button>
              <Button className="brand-bg text-white" onClick={() => impersonate(confirmImpersonate)} data-testid="sa-impersonate-confirm">{t("sa.impersonate_confirm")}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
          <DialogContent className="card-bg border-app" data-testid="sa-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("usr.delete_title_attr")}</DialogTitle>
              <DialogDescription className="t-secondary">{t("sa.delete_group_q")}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-app" onClick={() => setConfirmDelete(null)} data-testid="sa-delete-cancel">{t("common.cancel")}</Button>
              <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={() => removeGroup(confirmDelete)} data-testid="sa-delete-confirm">{t("usr.delete_title_attr")}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!confirmDeleteAccount} onOpenChange={(o) => { if (!o) setConfirmDeleteAccount(null); }}>
          <DialogContent className="card-bg border-app" data-testid="sa-delete-account-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("sa.delete_account")}</DialogTitle>
              <DialogDescription className="t-secondary">{confirmDeleteAccount?.owner_email} — {t("sa.delete_account_q")}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-app" onClick={() => setConfirmDeleteAccount(null)} data-testid="sa-delete-account-cancel">{t("common.cancel")}</Button>
              <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={() => removeAccount(confirmDeleteAccount)} data-testid="sa-delete-account-confirm">{t("sa.delete_account")}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!tconfirmImpersonate} onOpenChange={(o) => { if (!o) setTconfirmImpersonate(null); }}>
          <DialogContent className="card-bg border-app" data-testid="sa-space-impersonate-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("sa.impersonate")}</DialogTitle>
              <DialogDescription className="t-secondary">{t("sa.impersonate_q")}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-app" onClick={() => setTconfirmImpersonate(null)} data-testid="sa-space-impersonate-cancel">{t("common.cancel")}</Button>
              <Button className="brand-bg text-white" onClick={() => impersonateSpace(tconfirmImpersonate)} data-testid="sa-space-impersonate-confirm">{t("sa.impersonate_confirm")}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!tconfirmDelete} onOpenChange={(o) => { if (!o) setTconfirmDelete(null); }}>
          <DialogContent className="card-bg border-app" data-testid="sa-space-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("usr.delete_title_attr")}</DialogTitle>
              <DialogDescription className="t-secondary">{t("sa.delete_space_q")}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-app" onClick={() => setTconfirmDelete(null)} data-testid="sa-space-delete-cancel">{t("common.cancel")}</Button>
              <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={() => removeSpace(tconfirmDelete)} data-testid="sa-space-delete-confirm">{t("usr.delete_title_attr")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
